import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { createCampaign, getUsers } from '../../services/api';
import FileUploadProgress from '../Common/FileUploadProgress';
import {
    UserIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    DocumentArrowUpIcon,
    PlusIcon,
    CheckIcon,
    ExclamationTriangleIcon,
    CloudArrowUpIcon,
    MusicalNoteIcon
} from '@heroicons/react/24/outline';

const CreateCampaign = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStage, setUploadStage] = useState('uploading');
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [uploadStartTime, setUploadStartTime] = useState(0);
    const [processedRecords, setProcessedRecords] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [validRecords, setValidRecords] = useState(0);
    const [invalidRecords, setInvalidRecords] = useState(0);
    const [duplicateRecords, setDuplicateRecords] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    // New state for database write progress
    const [recordsWritten, setRecordsWritten] = useState(0);
    const [recordsPending, setRecordsPending] = useState(0);
    const [writeProgress, setWriteProgress] = useState(0);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        userId: '',
        campaignType: 'SMS',
        campaignStatus: 'Ongoing',
        message: '',
        csvFile: null,
        audioFile: null,
        mediaFile: null,
        manualPhoneNumbers: '',
        phoneNumberOption: 'manual'
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [fileInfo, setFileInfo] = useState({
        csv: null,
        audio: null,
        media: null,
        validationStatus: null,
        statusInfo: '',
        totalRows: 0,
        headers: []
    });
    const [errors, setErrors] = useState({});
    const [uploadAttempts, setUploadAttempts] = useState(new Map()); // Track upload attempts by file hash
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [eventSource, setEventSource] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        // Cleanup SSE connection on component unmount
        return () => {
            disconnectProgressStream();
        };
    }, []);

    // Real-time progress connection via Server-Sent Events
    const connectToProgressStream = (campaignId) => {
        const token = localStorage.getItem('token');
        const url = `/api/campaigns/progress/${campaignId}?token=${encodeURIComponent(token)}`;

        const es = new EventSource(url);

        es.onopen = () => {};

        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'connected') {
                    // SSE connected
                } else if (data.type === 'progress') {
                    // Update progress with real backend data
                    setUploadProgress(data.progress || 0);
                    setUploadStage(data.stage || 'processing');
                    setProcessedRecords(data.processedRecords || 0);
                    setValidRecords(data.validRecords || 0);
                    setRecordsWritten(data.writtenRecords || 0);
                    setRecordsPending(data.pendingRecords || 0);

                    // Calculate write progress percentage
                    if (data.processedRecords > 0) {
                        const writePercentage = Math.round((data.writtenRecords / data.processedRecords) * 100);
                        setWriteProgress(writePercentage);
                    }
                } else if (data.type === 'error') {
                    setUploadError(data.message || 'Authentication error');
                    es.close();
                }
            } catch (error) {
                // SSE parse error - ignore
            }
        };

        es.onerror = () => {
            es.close();
        };

        setEventSource(es);
        return es;
    };

    // Cleanup SSE connection
    const disconnectProgressStream = () => {
        if (eventSource) {
            eventSource.close();
            setEventSource(null);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await getUsers();
            if (response.data.success) {
                setUsers(response.data.data.users);
            }
        } catch (error) {
            toast.error('Failed to load users');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'userId') {
            const user = users.find(u => u._id === value);
            setSelectedUser(user);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear specific error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'message' && formData.campaignType === 'Whatsapp' && errors.mediaFile) {
            setErrors(prev => ({
                ...prev,
                mediaFile: ''
            }));
        }
    };

    const handleFileChange = async (e, fileType) => {
        const file = e.target.files[0];
        if (!file) return;

        const fileKey = fileType === 'csv' ? 'csvFile' : fileType === 'audio' ? 'audioFile' : 'mediaFile';

        // File type validation
        if (fileType === 'csv') {
            const allowedTypes = ['.csv'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!allowedTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload only CSV files'
                }));
                return;
            }

            // Validate CSV content
            try {
                const validation = await validateCSVFile(file);
                setFileInfo(prev => ({
                    ...prev,
                    csv: {
                        name: file.name,
                        size: (file.size / 1024).toFixed(2) + ' KB',
                        totalNumbers: validation.totalNumbers,
                        invalidCount: validation.invalidCount,
                        invalidPhones: validation.invalidPhones,
                        status: 'valid'
                    }
                }));
            } catch (error) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: error.message
                }));
                return;
            }
        } else if (fileType === 'audio') {
            const allowedTypes = ['.mp3', '.wav', '.m4a', '.aac'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!allowedTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload only audio files (MP3, WAV, M4A, AAC)'
                }));
                return;
            }

            // File size validation for audio files (50MB limit)
            if (file.size > 50 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Audio file size must be less than 50MB'
                }));
                return;
            }

            setFileInfo(prev => ({
                ...prev,
                audio: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                    type: fileExtension.toUpperCase().substring(1),
                    status: 'valid'
                }
            }));
        } else if (fileType === 'media') {
            const imageTypes = ['.jpg', '.jpeg', '.png', '.gif'];
            const documentTypes = ['.pdf'];
            const videoTypes = ['.mp4', '.mov', '.avi'];
            const audioTypes = ['.mp3', '.wav', '.m4a', '.aac'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!imageTypes.includes(fileExtension) && !documentTypes.includes(fileExtension) && !videoTypes.includes(fileExtension) && !audioTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload image, PDF, video, or audio files'
                }));
                return;
            }

            // Different size limits for supported WhatsApp media types
            const isImage = imageTypes.includes(fileExtension);
            const isDocument = documentTypes.includes(fileExtension);
            const isVideo = videoTypes.includes(fileExtension);
            const isAudio = audioTypes.includes(fileExtension);
            const maxSizeImage = process.env.REACT_APP_MEDIA_MAX_SIZE_IMAGE || 1 * 1024 * 1024; // 1MB for images
            const maxSizeVideo = process.env.REACT_APP_MEDIA_MAX_SIZE_VIDEO || 16 * 1024 * 1024; // 16MB for videos
            const maxSizeDocument = process.env.REACT_APP_MEDIA_MAX_SIZE_DOCUMENT || 10 * 1024 * 1024; // 10MB for PDFs
            const maxSizeAudio = process.env.REACT_APP_MEDIA_MAX_SIZE_AUDIO || 50 * 1024 * 1024; // 50MB for audio

            if (isImage && file.size > maxSizeImage) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Image file size must be less than ${maxSizeImage / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isVideo && file.size > maxSizeVideo) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Video file size must be less than ${maxSizeVideo / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isDocument && file.size > maxSizeDocument) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `PDF file size must be less than ${maxSizeDocument / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isAudio && file.size > maxSizeAudio) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Audio file size must be less than ${maxSizeAudio / (1024 * 1024)}MB`
                }));
                return;
            }

            setFileInfo(prev => ({
                ...prev,
                media: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                    type: fileExtension.toUpperCase().substring(1),
                    mediaType: isImage ? 'Image' : isDocument ? 'PDF' : isVideo ? 'Video' : 'Audio',
                    status: 'valid'
                }
            }));
        }

        setFormData(prev => ({
            ...prev,
            [fileKey]: file
        }));

        // Clear error
        setErrors(prev => ({
            ...prev,
            [fileKey]: '',
            ...(fileType === 'media' && formData.campaignType === 'Whatsapp' ? { message: '' } : {})
        }));
    };

    // Helper function to create file hash for duplicate prevention
    const createFileHash = (file) => {
        return `${file.name}_${file.size}_${file.lastModified}`;
    };

    // Client-side CSV validation
    const validateCSVColumns = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    const lines = text.split('\n');
                    if (lines.length === 0) {
                        reject(new Error('File is empty'));
                        return;
                    }

                    // Get header row
                    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

                    const requiredColumns = ['phone number', 'timestamp', 'status', 'pulse (seconds)'];
                    const missingColumns = [];

                    for (const reqCol of requiredColumns) {
                        const found = headers.some(header =>
                            header.includes(reqCol.toLowerCase()) || reqCol.toLowerCase().includes(header)
                        );
                        if (!found) {
                            missingColumns.push(reqCol);
                        }
                    }

                    if (missingColumns.length > 0) {
                        reject(new Error(`Missing required columns: ${missingColumns.join(', ')}`));
                    } else {
                        resolve({
                            isValid: true,
                            totalRows: lines.length - 1, // Exclude header
                            headers: headers
                        });
                    }
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));

            // Read only first 1KB to check headers
            const slice = file.slice(0, 1024);
            reader.readAsText(slice);
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.userId) {
            newErrors.userId = 'Please select a user';
        }

        // Validate phone number input based on option
        if (formData.phoneNumberOption === 'csv') {
            if (!formData.csvFile) {
                newErrors.csvFile = 'Please upload a CSV file';
            } else if (fileInfo?.validationStatus === 'validating') {
                newErrors.csvFile = 'Please wait for file validation to complete.';
            }
        } else if (formData.phoneNumberOption === 'manual') {
            if (!formData.manualPhoneNumbers || !formData.manualPhoneNumbers.trim()) {
                newErrors.manualPhoneNumbers = 'Please enter phone numbers';
            } else {
                const validation = validateManualPhoneNumbers(formData.manualPhoneNumbers);
                if (validation.validPhones.length === 0) {
                    newErrors.manualPhoneNumbers = 'No valid phone numbers found';
                }
            }
        }

        // Validate campaign type specific requirements
        if (formData.campaignType === 'SMS' && !formData.message.trim()) {
            newErrors.message = 'Message is required for SMS campaigns';
        }

        if (formData.campaignType === 'Voice Call' && !formData.audioFile) {
            newErrors.audioFile = 'Audio file is required for voice call campaigns';
        }

        if (formData.campaignType === 'Whatsapp' && !formData.message.trim() && !formData.mediaFile) {
            newErrors.mediaFile = 'For WhatsApp, add either a message or a media file';
        }

        // Check if user has any credits available for the selected campaign type
        if (selectedUser) {
            const availableCredits = getAvailableCredits();
            const creditRate = getCurrentCreditRate();

            if (availableCredits < creditRate) {
                newErrors.credits = `Insufficient credits. Available: ${availableCredits}, Required per phone: ${creditRate}`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the form errors');
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        setUploadStage('uploading');
        setUploadError(null);
        setUploadStartTime(Date.now());

        try {
            const submitData = new FormData();
            submitData.append('userId', formData.userId);
            submitData.append('campaignType', formData.campaignType);
            submitData.append('campaignStatus', formData.campaignStatus);
            submitData.append('phoneNumberOption', formData.phoneNumberOption);

            if (formData.phoneNumberOption === 'csv') {
                submitData.append('csvFile', formData.csvFile);
                // Set initial estimates based on file info
                if (fileInfo && fileInfo.totalRows) {
                    setTotalRecords(fileInfo.totalRows);
                }
            } else {
                submitData.append('manualPhoneNumbers', formData.manualPhoneNumbers);
                setTotalRecords(phoneNumbers.length);
            }

            if (formData.message.trim()) {
                submitData.append('message', formData.message);
            }

            if (formData.audioFile) {
                submitData.append('audioFile', formData.audioFile);
            }

            if (formData.mediaFile) {
                submitData.append('mediaFile', formData.mediaFile);
            }

            let lastLoaded = 0;
            let lastTime = Date.now();

            // Use the configured API instance with auth interceptors and progress tracking
            const response = await createCampaign(submitData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);

                    // Calculate upload speed
                    const currentTime = Date.now();
                    const timeDiff = currentTime - lastTime;
                    const loadedDiff = progressEvent.loaded - lastLoaded;

                    if (timeDiff > 0) {
                        const speed = (loadedDiff / timeDiff) * 1000; // bytes per second
                        setUploadSpeed(speed);
                    }

                    lastLoaded = progressEvent.loaded;
                    lastTime = currentTime;

                    // Update stage based on progress
                    if (percentCompleted < 100) {
                        setUploadStage('uploading');
                    } else {
                        setUploadStage('parsing');
                    }
                }
            });

            // Real progress updates will come from the backend
            setUploadStage('parsing');

            // Set initial estimates for progress tracking
            if (formData.phoneNumberOption === 'csv' && fileInfo.csv) {
                setTotalRecords(fileInfo.csv.totalNumbers || 0);
                setRecordsPending(fileInfo.csv.totalNumbers || 0);
            } else {
                setTotalRecords(phoneNumbers.length);
                setRecordsPending(phoneNumbers.length);
            }

            // Start progress simulation after upload completes
            let processingSimulation;
            if (response.data.success) {
                const totalRecords = formData.phoneNumberOption === 'csv' && fileInfo.csv ?
                    fileInfo.csv.totalNumbers : phoneNumbers.length;

                setUploadStage('parsing');
                setTotalRecords(totalRecords);
                setRecordsPending(totalRecords);

                let processed = 0;
                let isCompleted = false;
                const increment = Math.max(1, Math.floor(totalRecords / 300)); // Slower progress

                processingSimulation = setInterval(() => {
                    if (isCompleted) {
                        clearInterval(processingSimulation);
                        return;
                    }

                    // Don't complete simulation until we're at 90% to leave room for real backend updates
                    const maxProgress = Math.floor(totalRecords * 0.9);
                    processed = Math.min(processed + increment, maxProgress);
                    setProcessedRecords(processed);

                    const progressPercent = (processed / totalRecords) * 100;

                    // Progress through stages based on completion percentage
                    if (progressPercent < 20) {
                        setUploadStage('parsing');
                    } else if (progressPercent < 40) {
                        setUploadStage('validating');
                    } else if (progressPercent < 65) {
                        setUploadStage('processing');
                    } else {
                        setUploadStage('writing');
                        const written = Math.floor((progressPercent - 65) / 25 * totalRecords);
                        setRecordsWritten(written);
                        setRecordsPending(Math.max(0, totalRecords - written));
                        setWriteProgress(Math.min(90, ((progressPercent - 65) / 25) * 100));
                    }
                }, 80); // Updates every 80ms
            }

            if (response.data.success) {
                const { data, message } = response.data;

                // Connect to SSE stream for real-time progress updates
                if (data.campaignId || data._id) {
                    const campaignId = data.campaignId || data._id;
                    connectToProgressStream(campaignId);
                }

                // Extract processing statistics from response
                if (data.totalContacts) setValidRecords(data.totalContacts);
                if (data.processingDetails?.validationSummary?.duplicateErrors) {
                    setDuplicateRecords(data.processingDetails.validationSummary.duplicateErrors);
                }
                if (data.processingDetails?.skippedRows) setInvalidRecords(data.processingDetails.skippedRows);
                if (data.totalContacts) setProcessedRecords(data.totalContacts);

                setUploadStage('completed');
                setUploadProgress(100);

                // Stop progress simulation and disconnect SSE
                if (processingSimulation) {
                    clearInterval(processingSimulation);
                }
                disconnectProgressStream();

                setTimeout(() => {
                    // Create detailed success message
                    let successMessage = message + '\n\n';
                    successMessage += `📊 Processing Summary:\n`;
                    successMessage += `✅ ${data.totalContacts} valid contacts processed\n`;
                    successMessage += `📱 ${data.uniquePhones} unique phone numbers\n`;
                    successMessage += `💰 ${data.creditsDeducted} credits deducted\n`;

                    if (data.processingDetails?.hasErrors) {
                        const details = data.processingDetails;
                        successMessage += `\n⚠️ ${details.skippedRows} rows skipped:\n`;
                        const summary = details.validationSummary;
                        if (summary.phoneErrors > 0) successMessage += `• ${summary.phoneErrors} invalid phone numbers\n`;
                        if (summary.statusErrors > 0) successMessage += `• ${summary.statusErrors} invalid status values\n`;
                        if (summary.timestampErrors > 0) successMessage += `• ${summary.timestampErrors} missing timestamps\n`;
                        if (summary.pulseErrors > 0) successMessage += `• ${summary.pulseErrors} invalid pulse values\n`;
                        if (summary.duplicateErrors > 0) successMessage += `• ${summary.duplicateErrors} duplicates\n`;
                    }

                    if (data.statistics) {
                        successMessage += `\n📈 Status Breakdown:\n`;
                        successMessage += `• ${data.statistics.sent || 0} sent\n`;
                        successMessage += `• ${data.statistics.delivered || 0} delivered\n`;
                        successMessage += `• ${data.statistics.failed || 0} failed`;
                    }

                    toast.success(successMessage, {
                        autoClose: 8000,
                        style: { whiteSpace: 'pre-line' }
                    });
                    navigate('/campaigns');
                }, 2000);
            } else {
                setUploadStage('error');
                setUploadError(response.data.message || 'Failed to create campaign');
                toast.error(response.data.message || 'Failed to create campaign');
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred while creating the campaign';
            setUploadStage('error');
            setUploadError(message);
            toast.error(message);

            // Cleanup on error
            disconnectProgressStream();
        } finally {
            // Don't reset loading immediately for better UX with progress modal
            setTimeout(() => {
                setLoading(false);
                setUploadProgress(0);
                setUploadStage('uploading');
                setUploadSpeed(0);
                setProcessedRecords(0);
                setValidRecords(0);
                setInvalidRecords(0);
                setDuplicateRecords(0);
                setRecordsWritten(0);
                setRecordsPending(0);
                setWriteProgress(0);
                setUploadError(null);
                disconnectProgressStream();
            }, 3000);
        }
    };

    const getAvailableCredits = () => {
        if (!selectedUser) return 0;

        switch (formData.campaignType) {
            case 'Voice Call':
                return selectedUser.voiceCallCredits;
            case 'SMS':
                return selectedUser.smsCredits;
            case 'Whatsapp':
                return selectedUser.whatsappCredits;
            default:
                return 0;
        }
    };

    const getCurrentCreditRate = () => {
        if (!selectedUser) return 1;

        switch (formData.campaignType) {
            case 'Voice Call':
                return selectedUser.creditRates?.voiceCallRate || 1;
            case 'SMS':
                return selectedUser.creditRates?.smsRate || 1;
            case 'Whatsapp':
                return selectedUser.creditRates?.whatsappRate || 1;
            default:
                return 1;
        }
    };

    const getEstimatedCost = () => {
        if (!fileInfo || !selectedUser) return 0;
        // This is just an estimate - actual cost will be calculated after file processing
        return getCurrentCreditRate();
    };

    const campaignTypeOptions = [
        { value: 'SMS', label: 'SMS', icon: PhoneIcon, description: 'Send text messages' },
        { value: 'Voice Call', label: 'Voice Call', icon: SpeakerWaveIcon, description: 'Make voice calls' },
        { value: 'Whatsapp', label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, description: 'Send WhatsApp messages' }
    ];

    const campaignStatusOptions = [
        { value: 'Ongoing', label: 'Ongoing', description: 'Campaign is active and running' },
        { value: 'Paused', label: 'Paused', description: 'Campaign is temporarily stopped' },
        { value: 'Completed', label: 'Completed', description: 'Campaign has finished' }
    ];

    const campaignTypeIcons = {
        'Voice Call': SpeakerWaveIcon,
        'SMS': PhoneIcon,
        'Whatsapp': ChatBubbleLeftRightIcon
    };

    const validateManualPhoneNumbers = (numbersText) => {
        if (!numbersText.trim()) {
            return { validPhones: [], invalidPhones: [], totalNumbers: 0 };
        }

        const phoneArray = numbersText.split(',').map(phone => phone.trim()).filter(phone => phone);
        const validPhones = [];
        const invalidPhones = [];

        phoneArray.forEach((phone, index) => {
            // Basic phone validation - digits with optional + and spaces
            const cleanPhone = phone.replace(/\s/g, '');
            if (/^[\+]?[\d\-\(\)]{10,15}$/.test(cleanPhone)) {
                validPhones.push(cleanPhone);
            } else {
                invalidPhones.push({ line: index + 1, phone });
            }
        });

        return {
            validPhones: [...new Set(validPhones)], // Remove duplicates
            invalidPhones,
            totalNumbers: validPhones.length
        };
    };

    const validateCSVFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    const lines = text.split('\n').filter(line => line.trim());

                    if (lines.length === 0) {
                        reject(new Error('File is empty'));
                        return;
                    }

                    if (lines.length < 2) {
                        reject(new Error('CSV file must have a header row and at least one data row'));
                        return;
                    }

                    // Check record count limit (500,000 records excluding header)
                    const recordCount = lines.length - 1; // Exclude header row
                    if (recordCount > 500000) {
                        reject(new Error(`CSV file contains ${recordCount.toLocaleString()} records. Maximum allowed is 500,000 records per upload.`));
                        return;
                    }

                    // Parse header row to find "Phone number" column
                    const headerLine = lines[0].trim();
                    const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

                    // Find the "Phone number" column index
                    const phoneColumnIndex = headers.findIndex(header =>
                        header.includes('phone number') ||
                        header.includes('phone_number') ||
                        header.includes('phonenumber') ||
                        header === 'phone'
                    );

                    if (phoneColumnIndex === -1) {
                        reject(new Error('CSV file must have a "Phone number" column. Found columns: ' + headers.join(', ')));
                        return;
                    }

                    // Extract phone numbers from the correct column
                    const phoneData = lines.slice(1).map(line => {
                        const columns = line.split(',');
                        return columns[phoneColumnIndex] ? columns[phoneColumnIndex].trim().replace(/"/g, '') : '';
                    }).filter(phone => phone);

                    // Validate phone numbers
                    const validPhones = [];
                    const invalidPhones = [];

                    phoneData.forEach((phone, index) => {
                        // Basic phone validation - digits with optional + and spaces
                        const cleanPhone = phone.replace(/\s/g, '');
                        if (/^[\+]?[\d\-\(\)]{10,15}$/.test(cleanPhone)) {
                            validPhones.push(cleanPhone);
                        } else {
                            invalidPhones.push({ line: index + 2, phone }); // +2 because we start from line 2 (after header)
                        }
                    });

                    if (validPhones.length === 0) {
                        reject(new Error('No valid phone numbers found in the "Phone number" column'));
                        return;
                    }

                    // Remove duplicates
                    const uniquePhones = [...new Set(validPhones)];

                    // Check if unique phone numbers exceed limit
                    if (uniquePhones.length > 500000) {
                        reject(new Error(`After removing duplicates, file contains ${uniquePhones.length.toLocaleString()} unique phone numbers. Maximum allowed is 500,000 records per upload.`));
                        return;
                    }

                    resolve({
                        totalNumbers: uniquePhones.length,
                        validPhones: uniquePhones,
                        invalidCount: invalidPhones.length,
                        invalidPhones: invalidPhones.slice(0, 5), // Show first 5 invalid numbers
                        phoneColumnIndex,
                        headerFound: headers[phoneColumnIndex]
                    });
                } catch (error) {
                    reject(new Error('Failed to parse CSV file: ' + error.message));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const handleCampaignTypeChange = (value) => {
        setFormData(prev => ({
            ...prev,
            campaignType: value
        }));

        // Clear specific error when user makes a selection
        if (errors.campaignType) {
            setErrors(prev => ({
                ...prev,
                campaignType: ''
            }));
        }
    };

    const handleCampaignStatusChange = (value) => {
        setFormData(prev => ({
            ...prev,
            campaignStatus: value
        }));
    };

    const handlePhoneNumberOptionChange = (option) => {
        setFormData(prev => ({
            ...prev,
            phoneNumberOption: option,
            csvFile: null,
            manualPhoneNumbers: ''
        }));
        setFileInfo(null);
        setPhoneNumbers([]);

        // Clear related errors
        setErrors(prev => ({
            ...prev,
            csvFile: '',
            manualPhoneNumbers: ''
        }));
    };

    const renderCampaignTypeSelector = () => {
        return (
            <div className="selector-grid">
                {campaignTypeOptions.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = formData.campaignType === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleCampaignTypeChange(option.value)}
                            className={`p-3 sm:p-4 border-2 rounded-lg text-left transition-all duration-200 ${isSelected
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                }`}
                        >
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <IconComponent className={`h-5 w-5 sm:h-6 sm:w-6 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm sm:text-base truncate">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-500 truncate">{option.description}</div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderCampaignStatusSelector = () => {
        return (
            <div className="selector-grid">
                {campaignStatusOptions.map((option) => {
                    const isSelected = formData.campaignStatus === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleCampaignStatusChange(option.value)}
                            className={`p-3 sm:p-4 border-2 rounded-lg text-left transition-all duration-200 ${isSelected
                                ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                }`}
                        >
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <div className={`h-3 w-3 rounded-full ${option.value === 'Ongoing' ? (isSelected ? 'bg-green-500' : 'bg-gray-400') :
                                    option.value === 'Paused' ? (isSelected ? 'bg-yellow-500' : 'bg-gray-400') :
                                        (isSelected ? 'bg-blue-500' : 'bg-gray-400')
                                    }`}></div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm sm:text-base truncate">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-500 truncate">{option.description}</div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="page-container">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="heading-responsive">Add New Campaign</h1>
                    <p className="subheading-responsive">Create and launch a new communication campaign</p>
                </div>
            </div>

            {/* Form */}
            <div className="card-responsive">
                <form onSubmit={handleSubmit} className="form-section">
                    {/* User Selection */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            <UserIcon className="inline h-4 w-4 mr-1" />
                            Select User:
                        </label>
                        <select
                            name="userId"
                            value={formData.userId}
                            onChange={handleInputChange}
                            className={`input-field ${errors.userId ? 'border-red-500' : ''}`}
                            required
                        >
                            <option value="">Choose a user...</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                        {errors.userId && <p className="text-red-500 text-sm mt-1">{errors.userId}</p>}

                        {/* Show user credits */}
                        {selectedUser && (
                            <div className="mt-2 p-3 bg-soft-50 rounded-lg">
                                <p className="text-sm text-soft-600 font-medium">Available Credits:</p>
                                <div className="credits-grid mt-1">
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-blue-600">{selectedUser.voiceCallCredits}</p>
                                        <p className="text-xs text-soft-500">Voice Call</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-green-600">{selectedUser.smsCredits}</p>
                                        <p className="text-xs text-soft-500">SMS</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-purple-600">{selectedUser.whatsappCredits}</p>
                                        <p className="text-xs text-soft-500">WhatsApp</p>
                                    </div>
                                </div>

                                <p className="text-sm text-soft-600 font-medium mt-3">Credit Rates (Per Phone Number):</p>
                                <div className="credits-grid mt-1">
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-blue-600">{selectedUser.creditRates?.voiceCallRate || 1}</p>
                                        <p className="text-xs text-soft-500">Voice Call Rate</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-green-600">{selectedUser.creditRates?.smsRate || 1}</p>
                                        <p className="text-xs text-soft-500">SMS Rate</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-purple-600">{selectedUser.creditRates?.whatsappRate || 1}</p>
                                        <p className="text-xs text-soft-500">WhatsApp Rate</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Campaign Type */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            Campaign Type *
                        </label>
                        {renderCampaignTypeSelector()}
                    </div>

                    {/* Credit Rate Display */}
                    {selectedUser && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                Credit Rate (Auto-Applied)
                            </label>
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-blue-800">
                                        {formData.campaignType} Campaign Rate:
                                    </span>
                                    <span className="text-lg font-bold text-blue-600">
                                        {getCurrentCreditRate()} credits per phone number
                                    </span>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">
                                    This rate will be automatically applied based on the number of phone numbers in your file.
                                </p>
                            </div>
                            <div className="mt-2 text-sm">
                                <span className="text-gray-600">Available Credits: </span>
                                <span className={`font-semibold ${getAvailableCredits() > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {getAvailableCredits()}
                                </span>
                                {errors.credits && <p className="text-red-500 text-sm mt-1">{errors.credits}</p>}
                            </div>
                        </div>
                    )}

                    {/* Campaign Status */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            Campaign Status *
                        </label>
                        {renderCampaignStatusSelector()}
                    </div>

                    {/* Phone Numbers Input Method Selection */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            <DocumentArrowUpIcon className="inline h-4 w-4 mr-1" />
                            Phone Numbers *
                        </label>

                        {/* Option Selection */}
                        <div className="mb-4">
                            <div className="flex space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="phoneNumberOption"
                                        value="manual"
                                        checked={formData.phoneNumberOption === 'manual'}
                                        onChange={(e) => handlePhoneNumberOptionChange(e.target.value)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Enter manually</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="phoneNumberOption"
                                        value="csv"
                                        checked={formData.phoneNumberOption === 'csv'}
                                        onChange={(e) => handlePhoneNumberOptionChange(e.target.value)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Upload CSV file</span>
                                </label>
                            </div>
                        </div>

                        {/* Manual Phone Number Entry */}
                        {formData.phoneNumberOption === 'manual' && (
                            <div>
                                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-800 mb-2">Manual Entry:</h4>
                                    <div className="text-xs text-blue-700 space-y-1">
                                        <div>• Enter phone numbers separated by commas</div>
                                        <div>• Supported formats: +1234567890, 1234567890, (123) 456-7890</div>
                                        <div>• Duplicates will be automatically removed</div>
                                    </div>
                                </div>

                                <textarea
                                    name="manualPhoneNumbers"
                                    value={formData.manualPhoneNumbers}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        // Real-time validation feedback
                                        const validation = validateManualPhoneNumbers(e.target.value);
                                        setPhoneNumbers(validation.validPhones);
                                    }}
                                    rows="6"
                                    className={`input-field ${errors.manualPhoneNumbers ? 'border-red-500' : ''}`}
                                    placeholder="Enter phone numbers separated by commas, e.g., +1234567890, 9876543210, (555) 123-4567"
                                />

                                {formData.manualPhoneNumbers && (
                                    <div className="mt-2 text-sm">
                                        {(() => {
                                            const validation = validateManualPhoneNumbers(formData.manualPhoneNumbers);
                                            return (
                                                <div className="p-2 bg-gray-50 rounded border">
                                                    <p className="text-green-600">
                                                        <strong>Valid numbers:</strong> {validation.validPhones.length}
                                                    </p>
                                                    {validation.invalidPhones.length > 0 && (
                                                        <p className="text-red-600">
                                                            <strong>Invalid numbers:</strong> {validation.invalidPhones.length}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {errors.manualPhoneNumbers && (
                                    <p className="text-red-500 text-sm mt-1">{errors.manualPhoneNumbers}</p>
                                )}
                            </div>
                        )}

                        {/* CSV File Upload */}
                        {formData.phoneNumberOption === 'csv' && (
                            <div>
                                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format Required:</h4>
                                    <div className="text-xs text-blue-700 space-y-1">
                                        <div>• Must have columns: Phone Number, Timestamp, Status, Pulse</div>
                                        <div>• <strong>Timestamp:</strong> Any date format (e.g., 2024-02-02 10:30:00, 02/02/2024, DD-MM-YYYY HH:mm)</div>
                                        <div>• If timestamp is empty or invalid, current date/time will be used</div>
                                        <div>• Status values: sent, delivered, failed, pending</div>
                                        <div>• Column names are case-insensitive</div>
                                        <div>• Duplicates will be automatically detected</div>
                                        <div>• <strong>Maximum 500,000 records per upload</strong></div>
                                    </div>
                                </div>

                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'csv')}
                                    accept=".csv"
                                    className={`input-field ${errors.csvFile ? 'border-red-500' : ''}`}
                                    required
                                />
                                <p className="text-xs text-soft-500 mt-1">
                                    Upload a CSV file (Max 100MB, 500,000 records)
                                </p>

                                {fileInfo && (
                                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm font-medium text-green-800">File Selected</p>
                                            <span className={`px-2 py-1 text-xs rounded ${fileInfo.validationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                fileInfo.validationStatus === 'validating' ? 'bg-blue-100 text-blue-800' :
                                                    fileInfo.validationStatus === 'valid' ? 'bg-green-100 text-green-800' :
                                                        fileInfo.validationStatus === 'warning' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-red-100 text-red-800'
                                                }`}>
                                                {fileInfo.validationStatus === 'validating' ? 'Validating...' :
                                                    fileInfo.validationStatus === 'warning' ? 'Warning' :
                                                        fileInfo.validationStatus}
                                            </span>
                                        </div>
                                        <div className="space-y-1 text-sm text-green-700">
                                            <p><strong>Name:</strong> {fileInfo.name}</p>
                                            <p><strong>Size:</strong> {fileInfo.size}</p>
                                            <p><strong>Type:</strong> {fileInfo.type}</p>
                                        </div>
                                        <p className="text-xs text-green-600 mt-2">{fileInfo.statusInfo}</p>
                                    </div>
                                )}

                                {fileInfo?.validationStatus === 'warning' && (
                                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded">
                                        <div className="flex items-start">
                                            <div className="flex-shrink-0">
                                                <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-orange-800">File Upload Will Proceed</h3>
                                                <p className="text-sm text-orange-700 mt-1">
                                                    The system will skip invalid rows and process only valid data. You'll receive a detailed report after upload.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {errors.csvFile && (
                                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                        <p className="text-sm text-red-700">{errors.csvFile}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Message (for SMS/WhatsApp) */}
                    {(formData.campaignType === 'SMS' || formData.campaignType === 'Whatsapp') && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                Message {formData.campaignType === 'SMS' ? '*' : '(Optional)'}
                            </label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows="4"
                                className={`input-field ${errors.message ? 'border-red-500' : ''}`}
                                placeholder={formData.campaignType === 'Whatsapp' ? 'Enter message or upload media...' : 'Enter your message...'}
                                required={formData.campaignType === 'SMS'}
                            />
                            {errors.message && (
                                <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                            )}
                            {formData.campaignType === 'Whatsapp' && (
                                <p className="text-xs text-soft-500 mt-1">
                                    For WhatsApp, you can submit a message, a media file, or both.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Media Upload (for Voice Call) */}
                    {formData.campaignType === 'Voice Call' && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                <MusicalNoteIcon className="inline h-4 w-4 mr-1" />
                                Audio File (Optional)
                            </label>
                            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <h4 className="text-sm font-medium text-blue-800 mb-2">Audio File Requirements:</h4>
                                <div className="text-xs text-blue-700 space-y-1">
                                    <div>• Supported formats: MP3, WAV, M4A</div>
                                    <div>• Maximum file size: 50MB</div>
                                    <div>• Recommended length: 30 seconds to 3 minutes</div>
                                </div>
                            </div>
                            <input
                                type="file"
                                name="audioFile"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setFormData(prev => ({
                                            ...prev,
                                            audioFile: file
                                        }));
                                    }
                                }}
                                accept=".mp3,.wav,.m4a"
                                className="input-field"
                            />
                            <p className="text-xs text-soft-500 mt-1">
                                Upload an audio file for voice calls (Optional)
                            </p>
                        </div>
                    )}

                    {/* Media File Upload for WhatsApp */}
                    {formData.campaignType === 'Whatsapp' && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                <DocumentArrowUpIcon className="inline h-4 w-4 mr-1" />
                                Media File {formData.message.trim() ? '(Optional)' : '(Optional if message added)'}
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'media')}
                                    accept=".jpg,.jpeg,.png,.gif,.pdf,.mp4,.mov,.avi,.mp3,.wav,.m4a,.aac"
                                    className="hidden"
                                    id="mediaFile"
                                />
                                <label
                                    htmlFor="mediaFile"
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mb-2" />
                                    <span className="text-sm font-medium text-gray-700">Click to upload media file</span>
                                    <span className="text-xs text-gray-500">Images, PDF, video, and audio are supported</span>
                                </label>
                            </div>
                            {fileInfo && fileInfo.media && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <CheckIcon className="h-5 w-5 text-green-600" />
                                        <div>
                                            <p className="text-sm font-medium text-green-800">{fileInfo.media.name}</p>
                                            <p className="text-xs text-green-600">
                                                {fileInfo.media.size} • {fileInfo.media.type} • {fileInfo.media.mediaType}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {errors.mediaFile && <p className="text-red-500 text-sm mt-1">{errors.mediaFile}</p>}
                        </div>
                    )}

                    {/* Enhanced Upload Progress Modal - Only show for CSV uploads */}
                    {loading && formData.phoneNumberOption === 'csv' && (
                        <FileUploadProgress
                            progress={uploadProgress}
                            stage={uploadStage}
                            fileName={formData.csvFile?.name || ''}
                            fileSize={formData.csvFile?.size || 0}
                            uploadSpeed={uploadSpeed}
                            processedRecords={processedRecords}
                            totalRecords={totalRecords}
                            validRecords={validRecords}
                            invalidRecords={invalidRecords}
                            duplicateRecords={duplicateRecords}
                            recordsWritten={recordsWritten}
                            recordsPending={recordsPending}
                            writeProgress={writeProgress}
                            error={uploadError}
                            onCancel={() => {
                                setLoading(false);
                                setUploadProgress(0);
                                setUploadStage('uploading');
                                setUploadSpeed(0);
                                setProcessedRecords(0);
                                setValidRecords(0);
                                setInvalidRecords(0);
                                setDuplicateRecords(0);
                                setRecordsWritten(0);
                                setRecordsPending(0);
                                setWriteProgress(0);
                                setUploadError(null);
                            }}
                        />
                    )}

                    {/* Simple Loading Modal for Manual Entry */}
                    {loading && formData.phoneNumberOption === 'manual' && (
                        <div className="upload-modal fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className="modal-content bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fade-in">
                                <div className="p-6">
                                    <div className="flex flex-col items-center text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Campaign</h3>
                                        <p className="text-sm text-gray-600">
                                            Processing {phoneNumbers.length} phone numbers...
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cost Estimate */}
                    {((formData.phoneNumberOption === 'csv' && fileInfo) || (formData.phoneNumberOption === 'manual' && phoneNumbers.length > 0)) && selectedUser && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                            <h3 className="font-medium text-gray-800 mb-2">Cost Estimate</h3>
                            <p className="text-sm text-gray-600">
                                Credit rate per number: <span className="font-semibold">{getCurrentCreditRate()} credits</span>
                            </p>
                            {formData.phoneNumberOption === 'manual' && (
                                <p className="text-sm text-gray-600">
                                    Valid phone numbers: <span className="font-semibold">{phoneNumbers.length}</span>
                                </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                * Final cost will be calculated based on unique valid numbers
                            </p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="button-group-end">
                        <button
                            type="button"
                            onClick={() => navigate('/campaigns')}
                            className="btn-secondary w-full sm:w-auto"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto ${loading ? 'cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span className="hidden sm:inline">Creating...</span>
                                    <span className="sm:hidden">Creating</span>
                                </>
                            ) : (
                                <>
                                    <PlusIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Create Campaign</span>
                                    <span className="sm:hidden">Create</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div >
        </div >
    );
};

export default CreateCampaign; 